import { SellarImportsPayoutCreateRequestType } from "@sellar-io/api-v2/types";
import { Alert } from "../../../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { useSellarImportPayoutCreate } from "../hooks/useSellarImportPayoutCreate";
import { useForm, useWatch } from "react-hook-form";
import { useEffect } from "react";
import {
  Button,
  InputDate,
  InputNumber,
  InputCurrency,
} from "@sellar-io/components";

export const CreateSellarPayoutDialog = ({
  active,
  onDismiss,
}: {
  active: boolean;
  onDismiss: () => void;
}) => {
  const { reset, ...formHook } = useForm<SellarImportsPayoutCreateRequestType>({
    shouldUseNativeValidation: true,
  });

  useEffect(() => {
    if (active) {
      reset({
        captureStart: undefined,
        captureEnd: undefined,
        draughtHandlingFee: undefined,
        recurringCustomerFeePercentage: undefined,
        packagedHandlingFee: undefined,
      });
    }
  }, [active, reset]);

  const { captureStart, captureEnd } = useWatch({
    control: formHook.control,
  });

  const sellarImportPayoutCreateMutation = useSellarImportPayoutCreate();

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Create payout"}
        description={"Specify payout capture window and associated fees"}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <form
        onSubmit={formHook.handleSubmit((data) => {
          const draughtHandlingFeePence = data.draughtHandlingFee * 100;
          const packagedHandlingFeePence = data.packagedHandlingFee * 100;

          sellarImportPayoutCreateMutation.mutate(
            {
              ...data,
              draughtHandlingFee: draughtHandlingFeePence,
              packagedHandlingFee: packagedHandlingFeePence,
            },
            {
              onSuccess: () => onDismiss(),
            },
          );
        })}
      >
        <DialogContent>
          {sellarImportPayoutCreateMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(sellarImportPayoutCreateMutation.error)
                  ?.response?.data.message ?? "Unknown error occurred."
              }
            />
          ) : null}

          <InputLabel>Recurring Customer Fee (%)</InputLabel>
          <InputContainer>
            <InputNumber
              {...formHook.register("recurringCustomerFeePercentage", {
                required: true,
                min: 0,
                valueAsNumber: true,
              })}
              step={"0.01"}
            />
          </InputContainer>

          <InputLabel>Draught handling Fee (£)</InputLabel>
          <InputContainer>
            <InputCurrency
              showPrefix
              allowNegative
              {...formHook.register("draughtHandlingFee", {
                required: true,
                min: 0,
                valueAsNumber: true,
              })}
            />
          </InputContainer>

          <InputLabel>Packaged handling Fee (£)</InputLabel>
          <InputContainer>
            <InputCurrency
              showPrefix
              allowNegative
              {...formHook.register("packagedHandlingFee", {
                required: true,
                min: 0,
                valueAsNumber: true,
              })}
            />
          </InputContainer>

          <InputLabel>Capture start date</InputLabel>
          <InputContainer>
            <InputDate
              {...formHook.register("captureStart", {
                required: true,
                valueAsDate: true,
                value: captureStart,
              })}
            />
          </InputContainer>

          <InputLabel>Capture end date</InputLabel>
          <InputContainer>
            <InputDate
              {...formHook.register("captureEnd", {
                required: true,
                valueAsDate: true,
                value: captureEnd,
              })}
            />
          </InputContainer>
        </DialogContent>
        <DialogCTA>
          <Button
            loading={sellarImportPayoutCreateMutation.isPending}
            type="submit"
          >
            Create
          </Button>
        </DialogCTA>
      </form>
    </Dialog>
  );
};
